import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import type { CarReport, EnergySource, VehicleDefectProfile, VehicleRarity } from '../api/types'
import { sendReportAction } from '../api/usage'
import { useReportEngagement, useReportVisit } from '../api/useReportEngagement'
import {
  formatBoolean,
  formatColour,
  formatConsumption,
  formatDate,
  formatDateTime,
  formatDisplacement,
  formatElapsed,
  formatEuro,
  formatGearbox,
  formatLengthCm,
  formatList,
  formatLongDate,
  formatModelName,
  formatNumber,
  formatPower,
  formatYear,
  isPast,
  sentenceCase,
  vehicleName,
} from '../domain/format'
import { BrandLogo } from './BrandLogo'
import { Icon } from './Icon'
import { InspectionTimeline } from './InspectionTimeline'
import { LicensePlate } from './LicensePlate'
import { PlateSearch } from './PlateSearch'
import { isValuedByKoerslijst, KOERSLIJST_URL } from '../domain/valuation'
import { ChecklistSection } from './ChecklistSection'
import type { ChecklistHandover } from '../pages/ChecklistPage'
import { UnavailableSections } from './Notice'
import { PremiumBanner } from './PremiumBanner'
import { QuickCheck } from './QuickCheck'
import { RaritySection } from './RaritySection'
import { SectionNav } from './SectionNav'
import { DefectProfileSection } from './DefectProfileSection'
import {
  DEFECT_PROFILE_TITLE,
  defectProfileSubtitle,
  hasDefectProfile,
} from '../domain/defectProfile'
import { RecallList } from './RecallList'
import { ReviewSection } from './ReviewSection'
import { reviewSectionSubtitle, reviewSectionTitle } from '../domain/review'
import { ElectricSection } from './ElectricSection'
import { ELECTRIC_SECTION_TITLE, electricSectionSubtitle } from '../domain/electric'
import { OwnershipSection } from './OwnershipSection'
import { hasOwnershipCosts, OWNERSHIP_TITLE, ownershipSubtitle, roadTaxTitle } from '../domain/ownership'
import { VehicleTimeline } from './VehicleTimeline'
import { TIMELINE_SUBTITLE, TIMELINE_TITLE } from '../domain/timeline'
import { ZoneAccessSection } from './ZoneAccessSection'
import { ZONE_ACCESS_TITLE } from '../domain/zones'
import { DamageHistoryPrompt, DamageHistorySection } from './DamageHistorySection'
import { DAMAGE_HISTORY_SUBTITLE, DAMAGE_HISTORY_TITLE } from '../domain/damageReport'
import { isPremiumSection, markPremium } from '../domain/premium'
import { buildVehicleSummary } from '../domain/summary'
import { rows, type DataItem } from '../domain/rows'
import { Badge, DataRows, DataSubheading, EnergyLabel, NotRegistered, PremiumBlock, Section } from './Section'

interface SectionSpec {
  id: string
  title: string
  subtitle?: string
  /** Rendered only when this is true, so an empty section never becomes an empty card. */
  render: boolean
  content: ReactNode
}

/**
 * The whole report, as a sequence of white cards.
 *
 * **The order runs "what happened to this car" before "what this car is", and that is the point.**
 * A used-car buyer arrives with questions about condition — is the APK valid, does the mileage add
 * up, what has it failed on, are there recalls — and reads the specifications afterwards, if at all.
 * So the cards group as: the tiles, the damage gap, then condition and history, then the review as
 * the hinge, then the specifications, then money, then the two comparisons against other cars.
 *
 * That has a second effect worth protecting. Most of the premium content sits in the condition
 * group, so this ordering is also what puts the paid parts of the report where a reader sees them,
 * which is the whole purpose of giving them away during the test. The two pull the same way now and
 * will not once there is a real paywall: a report opening with four locked cards is the least
 * inviting thing this site could ship, so revisit the order when payment arrives.
 *
 * `rarity` and `defectProfile` each arrive after the report does, from their own endpoints, so both
 * sections are last regardless: appearing anywhere higher would reflow the page under a reader who
 * has already started. They are ordered by how long they take — rarity needs about a second and a half, the
 * defect profile two to five — so each appends below what is already on screen instead of pushing
 * the other one down.
 */
export function ReportView({
  report,
  rarity,
  defectProfile,
}: {
  report: CarReport
  rarity?: VehicleRarity
  defectProfile?: VehicleDefectProfile
}) {
  const { identity, registration, technical, powertrain, environmental, financial, inspection, odometer } = report
  const openChecklist = useOpenChecklist(report, defectProfile)

  // The manufacturer's own model names earn a row only when they say something the registration's
  // handelsbenaming does not. For plenty of cars the type approval carries that same single name,
  // and printing it twice looks like a rendering bug rather than a second fact.
  const manufacturerModelNames = formatList(identity.manufacturerModelNames)
  const extraModelNames =
    manufacturerModelNames?.toLowerCase() === identity.tradeName?.toLowerCase() ? undefined : manufacturerModelNames

  const withdrawal = identity.typeApprovalWithdrawal

  const basics = rows(
    // `<NotRegistered />` is granted to the few things every vehicle visibly has. Their absence is
    // a fact worth stating, where a silently missing row reads as a bug in this page. Everything
    // else keeps the default and disappears when the RDW has nothing; see `NotRegistered` for why
    // that stays the default.
    // Cased for reading. The register shouts the make and model and writes the body fields in
    // whatever case it feels like, and a table of ordinary values with `PEUGEOT` and
    // `gesloten opbouw` in it looks like two different bugs. These rows are cased the way
    // `formatColour` cases colours, and so is the heading above them.
    { label: 'Merk', value: formatModelName(identity.brand) ?? <NotRegistered /> },
    { label: 'Handelsbenaming', value: formatModelName(identity.tradeName) ?? <NotRegistered /> },
    { label: 'Handelsbenaming fabrikant', value: formatModelName(extraModelNames) },
    { label: 'Type', value: identity.type },
    { label: 'Variant', value: identity.variantCode },
    { label: 'Uitvoering', value: identity.editionCode },
    { label: 'Voertuigsoort', value: sentenceCase(identity.vehicleKind) ?? <NotRegistered /> },
    { label: 'Inrichting', value: sentenceCase(identity.bodyDescription) },
    { label: 'Europese voertuigcategorie', value: identity.europeanVehicleCategory },
    { label: 'Carrosserietype', value: formatList(identity.bodyTypes?.map(toSentenceCase)) },
    { label: 'Voertuigklasse', value: formatList(identity.vehicleClasses?.map(toSentenceCase)) },
    { label: 'Kleur', value: formatColour(identity.primaryColour) ?? <NotRegistered /> },
    { label: 'Kleur 2', value: formatColour(identity.secondaryColour) },
    // The approval number stays here, directly above the withdrawal row that qualifies it. Split
    // them and "Ingetrokken sinds 1 januari 2021" appears with nothing to say what was withdrawn.
    { label: 'Type goedkeuringsnr.', value: identity.typeApprovalNumber },
    {
      label: 'Typegoedkeuring',
      value: withdrawal
        ? withdrawal.until
          ? `Ingetrokken van ${formatLongDate(withdrawal.since)} tot ${formatLongDate(withdrawal.until)}`
          : `Ingetrokken sinds ${formatLongDate(withdrawal.since)}`
        : undefined,
    },
  )

  // Three of these dates are only useful once a reader has done the arithmetic on them, so the page
  // does it: how old the car is, and how long it has been on this name. The second is the one a
  // buyer actually reacts to, because a car that has changed hands three times in two years says
  // something the dates alone do not.
  const registrationRows = markPremium(rows(
    {
      label: 'Eerste toelating internationaal',
      value: withAside(formatDate(registration.firstAdmission), formatElapsed(registration.firstAdmission), 'oud') ?? (
        <NotRegistered />
      ),
    },
    { label: 'Eerste toelating in Nederland', value: formatDate(registration.firstAdmissionInNetherlands) },
    {
      label: 'Tenaamstelling sinds',
      value: withAside(
        formatDate(registration.currentRegistrationSince),
        formatElapsed(registration.currentRegistrationSince),
        'op deze naam',
      ),
    },
    { label: 'Bouwjaar', value: formatYear(registration.constructionYear) ?? <NotRegistered /> },
    { label: 'Geïmporteerd', value: formatBoolean(registration.isImported) },
    { label: 'Geëxporteerd', value: formatBoolean(registration.isExported) },
    { label: 'Tenaamstellen mogelijk', value: formatBoolean(registration.canBeTransferred) },
    { label: 'WAM-verzekerd', value: formatBoolean(registration.isInsured) },
    { label: 'Taxi', value: formatBoolean(registration.isTaxi) },
    { label: 'Openstaande terugroepactie', value: formatBoolean(registration.hasOpenRecall) },
  ))

  const technicalRows = rows(
    { label: 'Aantal zitplaatsen', value: formatNumber(technical.numberOfSeats) },
    { label: 'Aantal rolstoelplaatsen', value: formatNumber(technical.numberOfWheelchairSpaces) },
    { label: 'Aantal deuren', value: formatNumber(technical.numberOfDoors) },
    { label: 'Aantal wielen', value: formatNumber(technical.numberOfWheels) },
    { label: 'Aantal assen', value: formatNumber(technical.numberOfAxles) },
    { label: 'Lengte', value: formatLengthCm(technical.lengthMm) },
    { label: 'Breedte', value: formatLengthCm(technical.widthMm) },
    { label: 'Hoogte', value: formatLengthCm(technical.heightMm) },
    { label: 'Wielbasis', value: formatLengthCm(technical.wheelbaseMm) },
    { label: 'Massa leeg', value: formatNumber(technical.emptyMassKg, 'kg') },
    { label: 'Massa rijklaar', value: formatNumber(technical.drivingReadyMassKg, 'kg') },
    { label: 'Maximaal toegestane massa', value: formatNumber(technical.maximumMassKg, 'kg') },
    { label: 'Maximale massa samenstel', value: formatNumber(technical.maximumCombinationMassKg, 'kg') },
    { label: 'Max. trekgewicht ongeremd', value: formatNumber(technical.maximumTowingMassUnbrakedKg, 'kg') },
    { label: 'Max. trekgewicht geremd', value: formatNumber(technical.maximumTowingMassBrakedKg, 'kg') },
    { label: 'Laadvermogen', value: formatNumber(technical.loadCapacityKg, 'kg') },
    { label: 'Topsnelheid', value: formatNumber(technical.topSpeedKmh, 'km/u') },
    { label: 'Wettelijke maximumsnelheid', value: formatNumber(technical.restrictedTopSpeedKmh, 'km/u') },
    { label: 'Vermogen/massa-verhouding', value: formatNumber(technical.powerToWeightRatio, 'kW/kg') },
    // Where the VIN is stamped is a fact about the physical vehicle — the thing you go and look for
    // under the bonnet — not about its identity on paper.
    { label: 'Plaats chassisnummer', value: identity.chassisNumberLocation },
  )

  const powertrainRows = rows(
    { label: 'Vermogen', value: formatPower(powertrain.maximumPowerKw, powertrain.maximumPowerHp) },
    // With the cylinders and the gearbox rather than with the colour: it identifies the engine.
    { label: 'Motorcode', value: identity.engineCode },
    { label: 'Cilinderinhoud', value: formatDisplacement(powertrain.engineCapacityCc) },
    { label: 'Aantal cilinders', value: formatNumber(powertrain.numberOfCylinders) },
    { label: 'Cilinderopstelling', value: powertrain.cylinderLayout },
    { label: 'Soort transmissie', value: formatGearbox(powertrain.gearboxType) },
    { label: 'Aantal versnellingen', value: formatNumber(powertrain.numberOfGears) },
    { label: 'Elektromotor aanwezig', value: formatBoolean(powertrain.hasElectricMotor) },
    { label: 'Hybride', value: formatBoolean(powertrain.isHybrid) },
    { label: 'Extern oplaadbaar', value: formatBoolean(powertrain.isExternallyChargeable) },
    { label: 'Soort hybride', value: powertrain.hybridClass },
  )

  const energySources = powertrain.energySources ?? []

  const environmentalRows = rows(
    {
      label: 'Energielabel',
      value: environmental.energyLabel ? <EnergyLabel value={environmental.energyLabel} /> : undefined,
    },
    { label: 'CO₂-uitstoot gecombineerd', value: formatNumber(environmental.co2CombinedGramsPerKm, 'g/km') },
    { label: 'CO₂-uitstoot WLTP', value: formatNumber(environmental.co2CombinedWltpGramsPerKm, 'g/km') },
    { label: 'Emissiecode', value: environmental.emissionCode },
    { label: 'Uitstootklasse', value: environmental.emissionStandard },
    { label: 'Milieuklasse EG-goedkeuring', value: environmental.environmentalClass },
    { label: 'Emissie deeltjes WLTP', value: formatNumber(environmental.particulateEmissionWltp) },
    { label: 'Geluidsniveau stationair', value: formatNumber(environmental.noiseLevelStationaryDb, 'dB(A)') },
    { label: 'Toerental bij geluidsmeting', value: formatNumber(environmental.noiseLevelStationaryRpm, 'tpm') },
    { label: 'Geluidsniveau rijdend', value: formatNumber(environmental.noiseLevelDrivingDb, 'dB(A)') },
  )

  // The registered prices are what the car cost new; what a buyer wants is what it is worth now,
  // which the RDW does not publish. The ANWB Koerslijst answers that from the kenteken and the
  // current mileage in the reader's own browser, so the row is a link: nothing is read from it here.
  // Only for cars it can value, see `isValuedByKoerslijst`.
  const valuedByKoerslijst = isValuedByKoerslijst(registration)
  const financialRows = rows(
    { label: 'Catalogusprijs', value: formatEuro(financial.cataloguePriceEuro) },
    { label: 'Bruto BPM', value: formatEuro(financial.grossBpmEuro) },
    {
      label: 'Waarde nu',
      value: valuedByKoerslijst ? (
        <a href={KOERSLIJST_URL} target="_blank" rel="noopener noreferrer">
          Bereken in de ANWB Koerslijst
        </a>
      ) : undefined,
    },
  )

  const history = inspection.history ?? []
  const apkExpired = inspection.apkExpired ?? isPast(inspection.apkExpiryDate)

  // On a vehicle that has never been inspected the RDW's `vervaldatum_apk` is not an expiry at all:
  // it is the date the APK obligation starts, first admission plus the interval for the vehicle's
  // fuel (four years to the day for a petrol car). Labelling it "Vervaldatum APK" and stamping it
  // "Geldig" says the car holds an APK it has never had.
  //
  // An empty history is only trustworthy when the dataset was actually read. If it could not be,
  // the report says so in `unavailableSections`, and nothing here may be inferred from the silence.
  const neverInspected =
    history.length === 0 && !report.unavailableSections.includes('inspection.history')

  const inspectionRows = rows(
    {
      label: neverInspected ? 'APK-plicht vanaf' : 'Vervaldatum APK',
      value: inspection.apkExpiryDate ? (
        <>
          {formatDate(inspection.apkExpiryDate)}{' '}
          {neverInspected ? null : apkExpired === true ? (
            <Badge tone="negative">Verlopen</Badge>
          ) : apkExpired === false ? (
            <Badge tone="positive">Geldig</Badge>
          ) : null}
        </>
      ) : undefined,
    },
    // A count of zero on a car nobody has inspected reads as a clean record, which is the same
    // mistake the quick-check tile is careful not to make: no record is not the same as no findings.
    neverInspected
      ? { label: 'Keuringen', value: 'Nog niet gekeurd' }
      : { label: 'Totaal aantal bevindingen', value: formatNumber(inspection.totalDefectsFound) },
  )

  // `odometer.judgementCode` is deliberately not shown. It is the RDW's internal key for the
  // verdict — "05" — and the sentence it maps to is already the row below it, so the only thing it
  // adds to a reader is a number to wonder about.
  const odometerRows = rows(
    { label: 'Jaar laatste registratie', value: formatYear(odometer.lastRegistrationYear) },
    { label: 'Oordeel', value: odometer.judgement },
    { label: 'Toelichting', value: odometer.judgementExplanation, prose: true },
  )

  const timeline = report.timeline ?? []

  const sections: SectionSpec[] = [
    {
      id: 'overzicht',
      title: 'In één oogopslag',
      subtitle: 'APK, tellerstand, terugroepacties en meer',
      render: true,
      content: <QuickCheck report={report} />,
    },
    // Directly under the quick check, and that placement is the whole design of this section.
    //
    // It is the only thing on the site that earns anything, so it cannot be one unlabelled row at
    // the foot of a table, on a page most readers never reach the bottom of. And it belongs *here*
    // rather than merely high up: the tiles above have just told the reader the APK is valid and
    // the odometer is logical, which is precisely the moment the next question forms. What this
    // card answers is a real hole in the document rather than an advertisement dropped into it —
    // see `DamageHistorySection`.
    {
      id: 'schade',
      title: DAMAGE_HISTORY_TITLE,
      subtitle: DAMAGE_HISTORY_SUBTITLE,
      render: true,
      content: <DamageHistorySection report={report} />,
    },
    {
      id: 'apk',
      title: 'Onderhoud en APK',
      subtitle: neverInspected
        ? 'Wanneer de APK-plicht ingaat'
        : 'Vervaldatum en de keuringen van de afgelopen drie jaar',
      render: inspectionRows.length > 0 || history.length > 0,
      content: (
        <>
          <DataRows items={inspectionRows} />
          {history.length > 0 && (
            <PremiumBlock title="Keuringshistorie">
              <InspectionTimeline history={history} />
            </PremiumBlock>
          )}
          {/*
            The second way into the damage report, and the only other one. A reader who has just
            worked through what this car was pulled up on is thinking about its condition, which is
            as close as anyone on this page gets to being in the market for one — and "a rebuilt
            write-off passes the APK" is a fact this card is the right place to correct.
          */}
          <DamageHistoryPrompt report={report} />
        </>
      ),
    },
    {
      id: 'tellerstand',
      title: 'Tellerstandcontrole',
      subtitle: 'Het oordeel van de RDW over de geregistreerde kilometerstanden',
      render: odometerRows.length > 0,
      content: <DataRows items={odometerRows} />,
    },
    {
      id: 'terugroepacties',
      title: 'Terugroepacties',
      subtitle: `${report.recalls.length} bekend voor dit voertuig`,
      render: report.recalls.length > 0,
      content: <RecallList recalls={report.recalls} />,
    },
    {
      id: 'objecten',
      title: 'Toegevoegde objecten',
      subtitle: 'Wat er na de fabriek is ingebouwd',
      render: report.addedEquipment.length > 0,
      content: <AddedEquipmentRows report={report} />,
    },
    // After the condition cards because those are the answers the checklist fills in, and a reader
    // who has just worked through them is deciding whether to go and look at the car.
    {
      id: 'checklist',
      title: 'Checklist voor de bezichtiging',
      subtitle: '25 controles, al deels ingevuld voor deze auto',
      render: true,
      content: <ChecklistSection report={report} defectProfile={defectProfile} onOpen={openChecklist} />,
    },
    {
      id: 'registratie',
      title: 'Registratie en status',
      subtitle: 'Toelating, tenaamstelling en voertuigstatus',
      render: registrationRows.length > 0,
      content: <DataRows items={registrationRows} />,
    },
    // Closes the history group, because it is a recap: every list it merges — the registration
    // dates, the keuringen, the ingebouwde objecten, the terugroepacties — has been shown in full
    // directly above. Putting it first would ask the reader to recognise events they have not met.
    {
      id: 'tijdlijn',
      title: TIMELINE_TITLE,
      subtitle: TIMELINE_SUBTITLE,
      render: timeline.length > 0,
      // The defect profile is the last card on the page and the timeline's future entry is where a
      // reader is thinking about the next keuring, so the expiry links down to it — but only once
      // that card is known to have rendered, which is a request or two later than this.
      content: <VehicleTimeline entries={timeline} hasDefectProfile={hasDefectProfile(defectProfile)} />,
    },
    // The only prose in the report, and the hinge between what happened to this car and what the
    // car is. It stays high because it is the most readable thing here, but below the condition
    // cards: it is a review of the *model*, sometimes a decade older than the vehicle, and it must
    // not come before the facts about the one in front of the reader.
    {
      id: 'review',
      title: report.review ? reviewSectionTitle(report.review) : 'Beoordeling',
      subtitle: report.review ? reviewSectionSubtitle(report.review) : undefined,
      render: report.review !== undefined,
      content: report.review ? <ReviewSection review={report.review} /> : null,
    },
    {
      id: 'basisgegevens',
      title: 'Basisgegevens',
      subtitle: 'Merk, model, kleur en typegoedkeuring',
      render: basics.length > 0,
      content: (
        <>
          <DataRows items={basics} />
          {withdrawal && <TypeApprovalWithdrawalNote />}
        </>
      ),
    },
    {
      id: 'techniek',
      title: 'Technische gegevens',
      subtitle: 'Maten, gewichten en snelheid',
      render: technicalRows.length > 0,
      content: <DataRows items={technicalRows} />,
    },
    {
      id: 'motor',
      title: 'Motor en aandrijving',
      subtitle: 'Motor, transmissie en energiebronnen',
      render: powertrainRows.length > 0 || energySources.length > 0,
      content: (
        <>
          <DataRows items={powertrainRows} />
          {energySources.map((source) => (
            <EnergySourceRows key={source.sequence} source={source} multiple={energySources.length > 1} />
          ))}
        </>
      ),
    },
    // Directly after the powertrain card, which it extends: the RDW's own electric figures live
    // there, and these are the ones it does not publish. It is a separate card rather than more
    // rows in that one because it comes from a different source under a licence, and the credit
    // has to sit with the figures it belongs to.
    {
      id: 'accu',
      title: ELECTRIC_SECTION_TITLE,
      subtitle: report.electric ? electricSectionSubtitle(report.electric) : undefined,
      render: report.electric !== undefined,
      content: report.electric ? <ElectricSection electric={report.electric} /> : null,
    },
    {
      id: 'milieu',
      title: 'Milieu',
      subtitle: 'Uitstoot, verbruik en geluid',
      render: environmentalRows.length > 0,
      content: <DataRows items={environmentalRows} />,
    },
    // Straight after the emission figures it is derived from. It is the only card that says what
    // may be *done* with the vehicle rather than what it is, which is also why it refuses more than
    // it answers — see `ZoneAccessSection`.
    {
      id: 'milieuzones',
      title: ZONE_ACCESS_TITLE,
      subtitle: 'Waar dit voertuig nog naar binnen mag',
      render: report.zoneAccess !== undefined,
      content: report.zoneAccess ? <ZoneAccessSection zoneAccess={report.zoneAccess} /> : null,
    },
    {
      id: 'waarde',
      title: 'Waarde-informatie',
      // "Zoals geregistreerd" would claim the current value is registered too, so the subtitle
      // names both halves when the Koerslijst row is there.
      subtitle: valuedByKoerslijst
        ? 'De prijs bij eerste toelating, en waar je de waarde nu berekent'
        : 'Zoals geregistreerd bij eerste toelating',
      render: financialRows.length > 0,
      content: <DataRows items={financialRows} />,
    },
    // Next to the value card and deliberately not inside it: that one is what the vehicle cost when
    // it was new and is reported by the RDW, this one is what it costs to keep and is computed.
    // Mixing a registered figure with an estimate under one heading would lend the estimate
    // authority it has not got.
    {
      id: 'kosten',
      title: OWNERSHIP_TITLE,
      subtitle: ownershipSubtitle(financial.ownership, financial.roadTax),
      render: hasOwnershipCosts(financial.ownership, financial.roadTax),
      content: <OwnershipSection ownership={financial.ownership} roadTax={financial.roadTax} />,
    },
    {
      id: 'zeldzaamheid',
      title: 'Hoe bijzonder is deze auto?',
      subtitle: 'Hoeveel van dit model er in Nederland geregistreerd staan',
      render: rarity?.registeredInNetherlands !== undefined,
      content: rarity ? <RaritySection rarity={rarity} /> : null,
    },
    // Last, because it is the slowest of the three requests. It is also the only card about other
    // cars entirely — hence the title asks about "dit model" rather than about this vehicle, and
    // the subtitle names the sample rather than describing the contents.
    {
      id: 'modelgebreken',
      title: DEFECT_PROFILE_TITLE,
      subtitle: hasDefectProfile(defectProfile) ? defectProfileSubtitle(defectProfile) : undefined,
      render: hasDefectProfile(defectProfile),
      content: hasDefectProfile(defectProfile) ? <DefectProfileSection profile={defectProfile} /> : null,
    },
  ]

  const visible = sections.filter((section) => section.render)

  // How far down this report is read, and for how long. See `useReportEngagement`.
  useReportEngagement(visible.map((section) => section.id))

  // Whether the recall check produced an answer, which is not the same as there being a recall to
  // show. See `premiumTitles`.
  const recallCheckAnswered = registration.hasOpenRecall !== undefined || report.recalls.length > 0

  // What the banner names, walking the sections in the order they appear on the page.
  //
  // Not simply the premium cards, for two reasons.
  //
  // "Keuringshistorie" is a premium block inside the free APK card, and on a car with a long history
  // it is the largest single thing the paid report adds. The string has to match the `PremiumBlock`
  // title below. The road tax is the same shape: a premium block inside the free costs card, named
  // by the same constant its heading uses.
  //
  // "Terugroepacties" is named whenever the check answered, including when the answer is "Geen".
  // That is the one premium check whose *empty* result is the thing a buyer is paying for, and the
  // quick-check tile is already marked on the same grounds. The other premium cards are not treated
  // this way: nobody pays to be told a car has no tow bar.
  const premiumTitles = sections.flatMap((section) => {
    if (section.id === 'terugroepacties') {
      return recallCheckAnswered ? [section.title] : []
    }

    if (!section.render) {
      return []
    }

    if (isPremiumSection(section.id)) {
      return [section.title]
    }

    if (section.id === 'kosten') {
      return financial.roadTax ? [roadTaxTitle(financial.roadTax)] : []
    }

    return section.id === 'apk' && history.length > 0 ? ['Keuringshistorie'] : []
  })

  // A car can have nothing premium to show — no recalls, no retrofitted equipment, no odometer
  // verdict — and announcing a premium report above one would be selling something that is not on
  // the page. The row check covers the smaller marks that are not whole sections or blocks.
  const showPremiumBanner =
    premiumTitles.length > 0 ||
    [...registrationRows, ...energySources.flatMap(energySourceItems)].some((row) => row.premium)

  return (
    <>
      <ReportHeader report={report} onOpenChecklist={openChecklist} />

      {showPremiumBanner && <PremiumBanner sections={premiumTitles} />}

      <SectionNav sections={visible} />

      <UnavailableSections sections={report.unavailableSections} />

      {visible.map((section) => (
        <Section
          key={section.id}
          id={section.id}
          title={section.title}
          subtitle={section.subtitle}
          premium={isPremiumSection(section.id)}
        >
          {section.content}
        </Section>
      ))}

      {/* The report is served from a twelve-hour cache, so "as of when" is the honest counterpart to
          a page claiming everything on it comes straight from the RDW. At the foot because that is
          where a reader who wants provenance thinks to look, and where it costs nobody else a line. */}
      <p className="report__retrieved">
        Gegevens opgehaald bij de RDW op {formatDateTime(report.retrievedAt)}
      </p>

      <NextLookup />
    </>
  )
}

/**
 * The end of the report, and an invitation to check another car.
 *
 * A reader who reaches the bottom has finished the thing they came for, and the only options the
 * page offers them are the back button and the tab close. Somebody comparing two cars, which is
 * most of the people who read this far, then has to go and find the search box again.
 *
 * Deliberately not a `Section`: it is not part of the report, it does not belong in the quick-jump
 * navigation, and it does not print. The heading is a question rather than a slogan, because what
 * follows it is a field to type in.
 *
 * This is also where "vergelijkbare auto's" belongs when the catalogue exists to answer it: a
 * reader at the foot of a report is exactly the person who wants the next one, and suggestions
 * would sit above this box rather than replacing it.
 */
function NextLookup() {
  return (
    <section className="next-lookup" aria-labelledby="next-lookup-title">
      <h2 className="next-lookup__title" id="next-lookup-title">
        Nog een auto checken?
      </h2>
      <p className="next-lookup__body">
        Vul het volgende kenteken in en je hebt binnen een paar seconden het hele rapport. Gratis, en
        zonder account.
      </p>
      <div className="next-lookup__search">
        <PlateSearch />
      </div>
    </section>
  )
}

/**
 * The generated intro paragraph, or nothing when the RDW published too little to write one.
 *
 * The figures are bold so the paragraph can be skimmed as well as read: a reader should be able to
 * take the year, the price and the APK date off it without reading a sentence, which is the
 * difference between prose people read and prose people skip. `buildVehicleSummary` decides which
 * runs are values, because it is the thing that knows which came out of the report.
 */
function VehicleSummary({ report }: { report: CarReport }) {
  const segments = buildVehicleSummary(report)

  if (segments.length === 0) {
    return null
  }

  return (
    <p className="report__summary">
      {segments.map((segment, index) => {
        // A figure that refers to a card links to it, the same as the quick-check tiles do. The
        // condition sentence is the only one that carries any.
        if (segment.href) {
          return (
            <a className="report__summary-link" href={segment.href} key={index}>
              {segment.text}
            </a>
          )
        }

        return segment.value ? (
          <strong key={index}>{segment.text}</strong>
        ) : (
          <span key={index}>{segment.text}</span>
        )
      })}
    </p>
  )
}

/** `sentenceCase` for use in `map`, where the absent branch cannot happen. */
function toSentenceCase(value: string): string {
  return sentenceCase(value) ?? value
}

/**
 * A date with the elapsed time beside it, or just the date when that cannot be worked out.
 *
 * Returns `undefined` for an absent date so the row drops out like any other absent value, which is
 * the one thing this must not change: `rows()` decides what to show by asking whether the value is
 * `undefined`, and a `<>` wrapper around nothing would defeat it.
 */
function withAside(value: string | undefined, elapsed: string | undefined, suffix: string) {
  if (value === undefined) {
    return undefined
  }

  return elapsed === undefined ? (
    value
  ) : (
    <>
      {value}
      <span className="value-aside">
        ({elapsed} {suffix})
      </span>
    </>
  )
}

/**
 * Opens the fill-in checklist for this car, from the header button or the checklist card.
 *
 * The report goes along in the router state so the checklist page does not fetch it again, and the
 * click is sent as a `checklist` report action beside the navigation, never instead of it.
 */
function useOpenChecklist(report: CarReport, defectProfile?: VehicleDefectProfile) {
  const { viewId } = useReportVisit()
  const navigate = useNavigate()

  return () => {
    sendReportAction(viewId, 'checklist')
    const handover: ChecklistHandover = { report, defectProfile }
    navigate(`/checklist/${report.licensePlateCompact}`, { state: handover })
  }
}

function ReportHeader({ report, onOpenChecklist }: { report: CarReport; onOpenChecklist: () => void }) {
  const name = vehicleName(report.identity.brand, report.identity.tradeName)
  const photo = report.review?.photoUrl ? <ModelPhoto review={report.review} /> : null

  return (
    <header className="report__header">
      <div className="report__identity">
        <div className="report__name">
          <BrandLogo brand={report.identity.brand} vehicleKind={report.identity.vehicleKind} />
          <h1 className="report__title">{name}</h1>
        </div>

        <div className="report__actions">
          <button className="button button--quiet" type="button" onClick={() => window.print()}>
            <Icon name="print" size={16} />
            Print of sla op als PDF
          </button>
          {/* The shortcut to the checklist card further down, for a reader who already knows it
              is there. */}
          <button className="button button--quiet" type="button" onClick={onOpenChecklist}>
            <Icon name="check" size={16} />
            Checklist voor deze auto
          </button>
        </div>
      </div>

      {/*
        The car read back in a few sentences, in the header rather than inside the first card.
        It belongs with the name and the plate: all four are answering "what am I looking at", and
        a card lower a reader would have to cross a heading to reach the sentence that introduces
        everything under it. It is also the only prose on the page, which is what a
        search engine has to work with, and the head of the document is where that counts most.
      */}
      {/*
        Two columns only when there is a photo to put in the second one. Most models have no
        published review, and a fixed two-column grid would leave those reports with the summary
        squeezed into three fifths of the card and nothing beside it.
      */}
      <div className={`report__intro${photo ? ' report__intro--split' : ''}`}>
        <div className="report__lead">
          <VehicleSummary report={report} />
          {/* Under the paragraph rather than under the name. The heading already confirms which car
              this is, so the plate is free to close the block instead of splitting the title from
              the sentence that introduces it. */}
          <LicensePlate value={report.licensePlate} size="lg" />
        </div>

        {photo}
      </div>
    </header>
  )
}

/**
 * The publisher's press photo of the model, beside the summary.
 *
 * **The caption is the whole reason this is allowed here.** It is a photo of the *model*, not of
 * this car: usually a different colour, sometimes a different trim, and at the top of a page headed
 * by a registration plate a reader will otherwise take it for the vehicle in front of them. "Modelfoto"
 * says what it is in one word, and naming the publisher beside it credits the image where it is
 * shown. The full credit and the link back stay in the review card, which is where the API's
 * contract requires them.
 *
 * Only rendered when there is a review at all, so most reports do not have one. The host has to be
 * in the `img-src` of the CSP in `staticwebapp.config.json`; it currently lists the Autozine hosts,
 * and a second review provider would need adding there or its photos fail silently.
 */
function ModelPhoto({ review }: { review: NonNullable<CarReport['review']> }) {
  if (!review.photoUrl) {
    return null
  }

  return (
    <figure className="report__photo">
      <img
        src={review.photoUrl}
        alt={review.subject ? `Foto van een ${review.subject}` : 'Foto van het model'}
        loading="lazy"
        // The photo is hosted by the publisher, so it says nothing about who is reading it.
        referrerPolicy="no-referrer"
      />
      <figcaption>
        Modelfoto{review.subject ? ` van de ${review.subject}` : ''} · {review.source}
      </figcaption>
    </figure>
  )
}

/**
 * The rows for one energy source.
 *
 * Split out of the component below so `ReportView` can ask what is in them without rendering them
 * — it needs to know whether any premium row exists anywhere before it can decide to announce a
 * premium report. Asking here rather than re-listing the electric labels keeps `premium.ts` the
 * only place that knows which rows are paid content.
 */
function energySourceItems(source: EnergySource): DataItem[] {
  return markPremium(rows(
    { label: 'Vermogen', value: formatPower(source.maximumPowerKw, source.maximumPowerHp) },
    { label: 'Continu vermogen', value: formatNumber(source.continuousPowerKw, 'kW') },
    { label: 'Verbruik gecombineerd', value: formatConsumption(source.fuelConsumptionCombined) },
    { label: 'Verbruik stad', value: formatConsumption(source.fuelConsumptionUrban) },
    { label: 'Verbruik buitenweg', value: formatConsumption(source.fuelConsumptionExtraUrban) },
    { label: 'Verbruik gecombineerd WLTP', value: formatConsumption(source.fuelConsumptionCombinedWltp) },
    { label: 'Elektrisch verbruik', value: formatNumber(source.electricConsumptionWhPerKm, 'Wh/km') },
    { label: 'Actieradius elektrisch WLTP', value: formatNumber(source.electricRangeKm, 'km') },
    { label: 'Actieradius elektrisch stad WLTP', value: formatNumber(source.electricRangeUrbanKm, 'km') },
  ))
}

/**
 * One block per energy source. A plug-in hybrid has two, and merging them would lose exactly the
 * information that makes the car a hybrid — so each keeps its own consumption and range figures.
 */
function EnergySourceRows({ source, multiple }: { source: EnergySource; multiple: boolean }) {
  const items = energySourceItems(source)

  if (items.length === 0) {
    return null
  }

  return (
    <>
      <DataSubheading>
        {multiple ? `Energiebron ${source.sequence}: ` : ''}
        {source.description ?? 'Energiebron'}
      </DataSubheading>
      <DataRows items={items} />
    </>
  )
}

/**
 * What a withdrawn type approval does and does not mean.
 *
 * It travels with the row rather than being optional, because the phrase on its own reads as an
 * accusation against the car. It is not one: the approval is the model's paperwork, the vehicle
 * keeps its registration and its APK, and about one car in thirty carries a withdrawal.
 *
 * Hence no warning styling and no badge — the API's own note says a client that renders this as a
 * warning will be wrong most of the time. The date in the row above is what carries the meaning:
 * five in six withdrawals date from the administrative sweep of 1 January 2021, so a later date is
 * the interesting case, and this paragraph is what lets a reader make that distinction themselves.
 */
function TypeApprovalWithdrawalNote() {
  return (
    <p className="card__footnote">
      Een ingetrokken typegoedkeuring gaat over de papieren van dit model en niet over deze auto:
      het voertuig blijft gewoon geregistreerd en de APK verandert er niet door. Verreweg de meeste
      intrekkingen dateren van 1 januari 2021 en horen bij een administratieve omzetting van de
      Europese regelgeving. Een andere datum wijst er meestal op dat de intrekking alleen dit model
      betreft.
    </p>
  )
}

function AddedEquipmentRows({ report }: { report: CarReport }) {
  return (
    <DataRows
      items={report.addedEquipment.map((equipment, index) => ({
        label: equipment.description ?? `Object ${index + 1}`,
        value: (
          <>
            {equipment.fittedOn ? `Ingebouwd op ${formatDate(equipment.fittedOn)}` : 'Datum onbekend'}
            {equipment.gasTankCapacityLitres !== undefined &&
              `, tankinhoud ${formatNumber(equipment.gasTankCapacityLitres, 'liter')}`}
            {equipment.isRemoved && (
              <>
                {' '}
                <Badge tone="warning">Verwijderd</Badge>
              </>
            )}
          </>
        ),
      }))}
    />
  )
}
